import { useState, useEffect, useRef } from 'react';
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from '../firebase/firebase';
import { isFullUrl, isBase64, isRelativeUrl, isFirebaseFilename } from './appUtil';

// Cache for Firebase Storage URLs to avoid repeated API calls
const imageUrlCache = new Map();

/**
 * Processes a single image path and returns the appropriate URL
 * @param {string} imagePath - The image path (URL, base64, relative, or filename)
 * @returns {Promise<string>} - The processed image URL
 */
const processImage = async (imagePath) => {
    if (!imagePath) return imagePath;

    // If it's already a full URL, base64, or relative URL, return as-is
    if (isFullUrl(imagePath) || isBase64(imagePath) || isRelativeUrl(imagePath)) {
        return imagePath;
    }

    // If it's a Firebase filename, check cache first
    if (isFirebaseFilename(imagePath)) {
        if (imageUrlCache.has(imagePath)) {
            return imageUrlCache.get(imagePath);
        }

        try {
            const imageRef = ref(storage, imagePath);
            const url = await getDownloadURL(imageRef);
            // Cache the URL
            imageUrlCache.set(imagePath, url);
            return url;
        } catch (error) {
            console.error('Error fetching Firebase image URL:', error);
            // Return the original path as fallback
            return imagePath;
        }
    }

    // Fallback: return as-is
    return imagePath;
};

/**
 * Custom hook to process images (single or array) and generate Firebase Storage URLs when needed
 * @param {string|string[]|undefined} images - Single image path or array of image paths
 * @returns {[string|string[]|undefined, boolean]} - Processed images and loading state
 */
export const useFirebaseImage = (images) => {
    const [processedImages, setProcessedImages] = useState(images);
    const [isLoading, setIsLoading] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const processImages = async () => {
            if (!images) {
                setProcessedImages(images);
                return;
            }

            setIsLoading(true);

            try {
                if (Array.isArray(images)) {
                    // Process array of images
                    const processed = await Promise.all(
                        images.map(img => processImage(img))
                    );
                    if (isMountedRef.current) {
                        setProcessedImages(processed);
                    }
                } else {
                    // Process single image
                    const processed = await processImage(images);
                    if (isMountedRef.current) {
                        setProcessedImages(processed);
                    }
                }
            } catch (error) {
                console.error('Error processing images:', error);
                if (isMountedRef.current) {
                    setProcessedImages(images); // Fallback to original
                }
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        processImages();
    }, [images]);

    return [processedImages, isLoading];
};

/**
 * Utility function to process images synchronously (for non-hook usage)
 * Note: This will only work for non-Firebase images. For Firebase images, use the hook.
 * @param {string|string[]|undefined} images - Single image path or array of image paths
 * @returns {string|string[]|undefined} - Processed images (Firebase images will not be processed)
 */
export const processImageSync = (images) => {
    if (!images) return images;

    if (Array.isArray(images)) {
        return images.map(img => {
            if (isFullUrl(img) || isBase64(img) || isRelativeUrl(img)) {
                return img;
            }
            // For Firebase filenames, return as-is (needs async processing)
            return img;
        });
    }

    if (isFullUrl(images) || isBase64(images) || isRelativeUrl(images)) {
        return images;
    }

    return images;
};

